import { useState, useEffect } from 'react';
import { useSearchParams, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
} from '@mui/material';
import { toast } from 'react-toastify';
import { CheckCircle as CheckCircleIcon, Error as ErrorIcon } from '@mui/icons-material';
import { useAuthStore } from '@store/authStore';
import AuthPageShell from '@components/layout/AuthPageShell';
import AuthFormCard, { authLinkSx, authPrimaryCtaSx } from '@components/layout/AuthFormCard';
import { buildSignInPath } from '@utils/authRouting';
import { brandColors } from '@config/brandTokens';
import api from '@services/api';
import authService from '@services/authService';

const VerifyEmailPage = () => {
  const { setUser } = useAuthStore();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const pending = searchParams.get('pending') === '1';
  const email = String(searchParams.get('email') || '').trim();
  const returnUrl = searchParams.get('returnUrl');
  const [isLoading, setIsLoading] = useState(!!token);
  const [status, setStatus] = useState<'pending' | 'verifying' | 'success' | 'error'>(
    pending && !token ? 'pending' : 'verifying',
  );
  const [message, setMessage] = useState('');
  const [confirmedEmail, setConfirmedEmail] = useState(email);
  const [resendBusy, setResendBusy] = useState(false);
  const [devLink, setDevLink] = useState('');

  useEffect(() => {
    if (pending && !token) {
      const stored = sessionStorage.getItem('encycgames.verifyLink') || '';
      setDevLink(stored);
      setIsLoading(false);
      setStatus('pending');
      return;
    }
    if (!token) {
      setStatus('error');
      setMessage('Invalid or missing verification token.');
      setIsLoading(false);
      return;
    }

    verifyEmail();
  }, [token, pending]);

  const verifyEmail = async () => {
    try {
      const response = await fetch('/api/auth/verify-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });

      const result = await response.json();

      if (result.success) {
        sessionStorage.removeItem('encycgames.verifyLink');
        const fromApi = String(result.email || '').trim();
        if (fromApi) setConfirmedEmail(fromApi);
        // Drop any other browser session so Sign in cannot open that user's dashboard.
        authService.clearLocalSession();
        setUser(null);
        setStatus('success');
        setMessage('Email confirmed. Sign in to continue.');
        toast.success('Email confirmed. Sign in to continue.');
      } else {
        setStatus('error');
        setMessage(result.message || 'Failed to verify email');
        toast.error(result.message || 'Failed to verify email');
      }
    } catch (error: any) {
      console.error('Verify email error:', error);
      setStatus('error');
      setMessage('An error occurred while verifying your email. Please try again.');
      toast.error('An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const resend = async () => {
    if (!email) {
      toast.error('Add the email you registered with so we can resend the link.');
      return;
    }
    setResendBusy(true);
    try {
      await api.post('/auth/send-email-verification', { email });
      toast.success('If that inbox exists, we sent another verification link.');
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Could not resend the verification email.');
    } finally {
      setResendBusy(false);
    }
  };

  const verifiedEmail = confirmedEmail || email;
  const signInPath = buildSignInPath({ email: verifiedEmail, returnUrl });
  const verifiedSignInPath = buildSignInPath({
    email: verifiedEmail,
    returnUrl,
    registered: 'verified',
    fresh: true,
  });

  return (
    <AuthPageShell>
      <AuthFormCard
        roleLabel="Email"
        title={
          status === 'pending'
            ? 'Check your email'
            : status === 'success'
              ? 'Email verified'
              : status === 'error'
                ? 'Verification needed'
                : 'Verifying email'
        }
        subtitle={
          status === 'pending'
            ? 'Confirm the link we sent before you sign in.'
            : undefined
        }
        chrome
        backTo={signInPath}
      >
        {isLoading ? (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <CircularProgress size={40} sx={{ mb: 2 }} />
            <Typography variant="body2" color="text.secondary">
              Please wait while we verify your email address.
            </Typography>
          </Box>
        ) : status === 'pending' ? (
          <>
            <Alert severity="info" sx={{ mb: 2 }}>
              We sent a verification link{email ? ` to ${email}` : ''}. Open that email and confirm
              your address. You cannot sign in until this is done.
            </Alert>
            {devLink && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Local / test mail was skipped.{' '}
                <Button href={devLink} size="small">
                  Open verification link
                </Button>
              </Alert>
            )}
            <Button
              fullWidth
              variant="contained"
              sx={authPrimaryCtaSx}
              onClick={resend}
              disabled={resendBusy || !email}
            >
              {resendBusy ? 'Sending…' : 'Resend verification email'}
            </Button>
            <Typography variant="body2" sx={{ mt: 2.5, textAlign: 'center', ...authLinkSx }}>
              Already verified?{' '}
              <Button component={RouterLink} to={signInPath} size="small">
                Sign in
              </Button>
            </Typography>
          </>
        ) : status === 'success' ? (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <CheckCircleIcon sx={{ fontSize: 56, color: 'success.main' }} />
            </Box>
            <Alert severity="success" sx={{ mb: 2 }}>
              {message}
            </Alert>
            <Button
              fullWidth
              variant="contained"
              sx={authPrimaryCtaSx}
              component={RouterLink}
              to={verifiedSignInPath}
            >
              Sign in
            </Button>
          </>
        ) : (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <ErrorIcon sx={{ fontSize: 48, color: 'error.main' }} />
            </Box>
            <Alert severity="error" sx={{ mb: 2 }}>
              {message}
            </Alert>
            {email && (
              <Button fullWidth variant="contained" sx={{ ...authPrimaryCtaSx, mb: 1.5 }} onClick={resend} disabled={resendBusy}>
                Resend verification email
              </Button>
            )}
            <Button fullWidth variant="outlined" component={RouterLink} to={signInPath} sx={{ color: brandColors.navy }}>
              Already verified? Sign in
            </Button>
          </>
        )}
      </AuthFormCard>
    </AuthPageShell>
  );
};

export default VerifyEmailPage;
